/* StartEcom product fold (sections/se-product-main.liquid): gallery, variant switching, add to cart,
   sticky bar. Vanilla, no theme dependency. Without JS the form still posts to /cart/add. */
(() => {
  if (window.SePm) return;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Shopify's storefront standard events (Horizon's cart drawer listens to them). Optional:
  // resolves to null on themes without the import map entry.
  let eventsLib;
  const events = () => (eventsLib ??= import('@shopify/events').catch(() => null));

  class Gallery {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('[data-track]');
      this.slides = [...el.querySelectorAll('[data-slide]')];
      this.thumbs = [...el.querySelectorAll('.se-pm__thumb')];
      this.dots = [...el.querySelectorAll('.se-pm__dot')];
      this.count = el.querySelector('[data-count]');
      this.thumbStrip = el.querySelector('[data-thumbs]');
      this.i = 0;
      if (!this.track || this.slides.length < 2) return;
      let raf = 0;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      el.addEventListener('click', (e) => {
        const go = e.target.closest('[data-go]');
        const step = e.target.closest('[data-step]');
        if (go) this.go(+go.dataset.go);
        else if (step) this.go((this.i + +step.dataset.step + this.slides.length) % this.slides.length);
      });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.go(Math.max(0, Math.min(this.slides.length - 1, this.i + (e.key === 'ArrowRight' ? 1 : -1))));
        }
      });
    }
    // Grids (desktop photo grid, 2×2, phone mosaic) and the desktop one-column stack don't scroll
    // sideways: picking a photo brings it to the first (large) place instead.
    get isGrid() {
      const cs = getComputedStyle(this.track);
      return cs.display === 'grid' || cs.flexDirection === 'column';
    }
    nearest() {
      const t = this.track;
      const mid = t.scrollLeft + t.clientWidth / 2;
      let best = 0, dist = Infinity;
      this.slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid);
        if (d < dist) { dist = d; best = i; }
      });
      return best;
    }
    sync() {
      if (this.isGrid) return;
      this.mark(this.nearest());
    }
    mark(i) {
      this.i = i;
      this.el.style.setProperty('--i', i);
      if (this.count) this.count.textContent = i + 1;
      [...this.thumbs, ...this.dots].forEach((b) => {
        if (+b.dataset.go === i) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      const th = this.thumbs[i];
      const strip = this.thumbStrip;
      if (th && strip) {
        const behavior = reduce() ? 'auto' : 'smooth';
        if (strip.scrollHeight > strip.clientHeight + 1) strip.scrollTo({ top: th.offsetTop - (strip.clientHeight - th.offsetHeight) / 2, behavior });
        else if (strip.scrollWidth > strip.clientWidth) strip.scrollTo({ left: th.offsetLeft - (strip.clientWidth - th.offsetWidth) / 2, behavior });
      }
    }
    go(i, instant) {
      const s = this.slides[i];
      if (!s) return;
      if (this.isGrid) {
        // Desktop photo grid: bring the chosen photo to the large first cell.
        this.slides.forEach((x, j) => {
          x.style.order = x === s && i > 0 ? '-1' : '';
          x.classList.toggle('is-lead', i > 0 ? x === s : j === 0);
        });
        this.mark(i);
        return;
      }
      const t = this.track;
      t.scrollTo({ left: s.offsetLeft - (t.clientWidth - s.offsetWidth) / 2, behavior: instant || reduce() ? 'auto' : 'smooth' });
      this.mark(i);
    }
    goMedia(id, instant) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.go(i, instant);
    }
  }

  class ProductFold {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form.se-pm__form');
      this.atc = root.querySelector('[data-atc]');
      this.msg = root.querySelector('[data-atc-msg]');
      this.sticky = root.querySelector('[data-sticky]');
      this.gallery = new Gallery(root.querySelector('[data-pm-gallery]'));
      try { this.variants = JSON.parse(root.querySelector('[data-se-pm-variants]').textContent); } catch { this.variants = []; }
      root.classList.add('is-js');
      root.addEventListener('change', (e) => { if (e.target.matches('.se-pm__radio')) this.onOption(); });
      this.select = root.querySelector('.se-pm__vselect');
      if (this.form) this.form.addEventListener('submit', (e) => this.add(e));
      // The sticky button sits outside the form (form="…" covers no-JS); submit the form explicitly.
      root.querySelectorAll('[data-atc-mirror]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.form && this.atc) this.form.requestSubmit(this.atc);
      }));
      // Collapsed highlights only save room on phones; open them on desktop.
      if (window.matchMedia('(min-width: 990px)').matches) root.querySelectorAll('.se-pm__more').forEach((d) => { d.open = true; });
      // "Safety & care notes" under the button opens its fold further down.
      root.querySelectorAll('[data-safety-link]').forEach((a) => a.addEventListener('click', () => {
        const d = root.querySelector('[data-safety]');
        if (d) d.open = true;
      }));
      // Opened on a ?variant= link: start the gallery on that variant's photo.
      if (/[?&]variant=/.test(window.location.search)) {
        const v = this.current();
        if (v && v.media) this.gallery.goMedia(v.media, true);
      }
      this.watchSticky();
      this.watchDesktop();
      events();
    }

    current() {
      const id = this.select ? this.select.value : (this.form?.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]') || {}).value;
      return this.variants.find((v) => String(v.id) === String(id));
    }

    onOption() {
      const picked = [];
      this.root.querySelectorAll('.se-pm__radio:checked').forEach((r) => { picked[+r.dataset.optIndex] = r.dataset.optValue; });
      picked.forEach((val, i) => this.root.querySelectorAll(`[data-opt-label="${i}"]`).forEach((n) => { n.textContent = val; }));
      const v = this.variants.find((x) => x.options.every((o, i) => picked[i] === undefined || o === picked[i]));
      if (this.select && v) this.select.value = v.id;
      this.update(v);
    }

    update(v) {
      const label = this.root.querySelectorAll('[data-atc-label]');
      const buttons = [this.atc, ...this.root.querySelectorAll('[data-atc-mirror]')].filter(Boolean);
      const ok = Boolean(v && v.available);
      buttons.forEach((b) => { b.disabled = !ok; });
      label.forEach((l) => { l.textContent = ok ? l.dataset.text : v ? 'Sold out' : 'Unavailable'; });
      if (!v) return;
      this.root.querySelectorAll('[data-se-price]').forEach((n) => { n.textContent = v.price; });
      this.root.querySelectorAll('[data-se-compare]').forEach((n) => {
        n.hidden = !v.compare;
        if (v.compare) n.innerHTML = `<span class="se-sr">Regular price</span>${v.compare}`;
      });
      this.root.querySelectorAll('[data-variant-title]').forEach((n) => { n.textContent = v.title; });
      const img = this.sticky?.querySelector('.se-pm-sticky__img');
      if (img && v.image) { img.src = v.image; img.removeAttribute('srcset'); }
      if (v.media) this.gallery.goMedia(v.media);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
      this.root.dispatchEvent(new CustomEvent('se:variant-change', { bubbles: true, detail: { variant: v } }));
    }

    async add(e) {
      e.preventDefault();
      const v = this.current();
      if (!v || !v.available || this.busy) return;
      this.busy = true;
      const buttons = [this.atc, ...this.root.querySelectorAll('[data-atc-mirror]')].filter(Boolean);
      buttons.forEach((b) => { b.setAttribute('aria-busy', 'true'); });
      this.msg.textContent = '';
      const body = new FormData(this.form);
      body.set('id', v.id);
      if (!body.get('quantity')) body.set('quantity', '1');
      const cartUrl = this.root.dataset.cart || '/cart';
      // 1. Shopify standard actions (Horizon configures them: events fire, its drawer refreshes and opens).
      const actions = window.Shopify?.actions;
      if (actions?.updateCart) {
        try {
          const res = await actions.updateCart({ lines: [{ merchandiseId: String(v.id), quantity: 1 }] }, { event: { context: 'product' } });
          if (res?.userErrors?.length) throw Object.assign(new Error(res.userErrors[0].message), { shown: true });
          if (!document.querySelector('cart-drawer-component[auto-open]') && actions.openCart) await actions.openCart();
          this.flash('Added to cart');
        } catch (err) {
          this.msg.textContent = err.shown ? err.message : 'Something went wrong. Please try again.';
        } finally {
          this.busy = false;
          buttons.forEach((b) => b.removeAttribute('aria-busy'));
        }
        return;
      }
      // 2. AJAX cart + the storefront event Horizon's drawer listens to; 3. no drawer → cart page.
      const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((n) => n.dataset.sectionId);
      if (sections.length) body.set('sections', sections.join(','));
      const lib = await events();
      const drawer = document.querySelector('cart-drawer-component');
      let deferred = null;
      if (lib?.CartLinesUpdateEvent && drawer) {
        deferred = lib.CartLinesUpdateEvent.createPromise();
        this.form.dispatchEvent(new lib.CartLinesUpdateEvent({
          action: 'add', context: 'product', lines: [{ merchandiseId: String(v.id), quantity: 1 }], promise: deferred.promise,
        }));
      }
      const addUrl = `${(this.root.dataset.cartAdd || '/cart/add').replace(/\.js$/, '')}.js`;
      try {
        const res = await fetch(addUrl, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const data = await res.json();
        if (!res.ok || data.status) throw Object.assign(new Error(data.description || data.message || 'Could not add to cart'), { shown: true });
        if (!deferred) { window.location.href = cartUrl; return; }
        const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        deferred.resolve({
          cart: lib.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: 1, sections: data.sections, didError: false, source: 'se-product', productId: this.root.dataset.productId },
        });
        this.flash('Added to cart');
      } catch (err) {
        this.msg.textContent = err.shown ? err.message : 'Something went wrong. Please try again.';
        if (deferred) {
          fetch(`${cartUrl}.js`).then((r) => r.json()).then((cart) => deferred.resolve({
            cart: lib.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, itemCount: 0 },
          })).catch(() => deferred.reject(err));
        }
      } finally {
        this.busy = false;
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    }

    flash(text) {
      const labels = this.root.querySelectorAll('[data-atc-label]');
      labels.forEach((l) => { l.textContent = text; });
      clearTimeout(this.flashT);
      this.flashT = setTimeout(() => labels.forEach((l) => { l.textContent = l.dataset.text; }), 2000);
    }

    watchSticky() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const bar = this.sticky;
      const set = (on) => {
        bar.classList.toggle('is-on', on);
        bar.inert = !on;
        document.documentElement.classList.toggle('se-pm-sticky-on', on);
      };
      new IntersectionObserver(([en]) => set(!en.isIntersecting && en.boundingClientRect.top < 0)).observe(this.atc);
    }

    // Desktop: keep the buy box in view while the photos scroll; if the buy box is taller than the
    // screen, the gallery sticks instead.
    watchDesktop() {
      const info = this.root.querySelector('.se-pm__info');
      const gal = this.root.querySelector('[data-pm-gallery]');
      if (!info || !gal) return;
      const fit = () => {
        const room = window.innerHeight - 120;
        this.root.classList.toggle('is-stick-gallery', info.offsetHeight > room && gal.offsetHeight < info.offsetHeight);
      };
      fit();
      window.addEventListener('resize', fit, { passive: true });
      info.addEventListener('toggle', fit, true);
    }
  }

  const init = (scope = document) => scope.querySelectorAll('[data-se-pm]').forEach((r) => { if (!r.sePm) r.sePm = new ProductFold(r); });
  window.SePm = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
